import * as React from "react";
import { ChevronDown, Info } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import {
  REGIME_OVERLAY_RESEARCH_DISCLAIMER_EXPANDED,
  RESEARCH_DISCLAIMER_COLLAPSED,
  SIMULATOR_RESEARCH_DISCLAIMER_EXPANDED,
  type ResearchDisclaimerKind,
} from "../constants/researchDisclaimers";
import {
  readResearchDisclaimerExpanded,
  writeResearchDisclaimerExpanded,
} from "../utils/researchDisclaimerUiPersistence";

const EXPANDED_BY_KIND: Record<ResearchDisclaimerKind, string> = {
  simulator: SIMULATOR_RESEARCH_DISCLAIMER_EXPANDED,
  regime: REGIME_OVERLAY_RESEARCH_DISCLAIMER_EXPANDED,
};

const DISCLAIMER_BODY_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal leading-5 text-gray-500 dark:text-white";

export type ResearchDisclaimerNoticeProps = {
  kind: ResearchDisclaimerKind;
  className?: string;
  /** Optional override for tests / special surfaces. */
  expandedBody?: string;
};

type DisclaimerToggleButtonProps = {
  expanded: boolean;
  onClick: () => void;
  className?: string;
};

function DisclaimerToggleButton({
  expanded,
  onClick,
  className,
}: DisclaimerToggleButtonProps): React.ReactElement {
  return (
    <Button
      type="button"
      variant="link"
      size="sm"
      data-font="primary"
      data-research-disclaimer-toggle=""
      aria-expanded={expanded}
      aria-label={expanded ? "Show less disclaimer" : "Show more disclaimer"}
      className={cn(
        "h-auto min-h-0 shrink-0 justify-start gap-1 self-start p-0 no-underline hover:bg-transparent hover:no-underline",
        className
      )}
      style={{
        paddingInline: 0,
        paddingBlock: 0,
        height: "auto",
        color: "var(--brand-tropical-surf)",
      }}
      onClick={onClick}
    >
      <span className="normal-case tracking-normal">
        {expanded ? "Show less" : "Show more"}
      </span>
      <ChevronDown
        aria-hidden
        data-icon="inline-end"
        className={cn(
          "!size-3.5 !translate-y-0 shrink-0 text-[color:var(--brand-tropical-surf)] transition-transform duration-200",
          expanded && "rotate-180"
        )}
      />
    </Button>
  );
}

/**
 * Always-visible research disclaimer. Collapse remembers preference in localStorage
 * per kind; collapsed state still shows the short one-liner (never fully dismissable).
 */
export function ResearchDisclaimerNotice({
  kind,
  className,
  expandedBody,
}: ResearchDisclaimerNoticeProps): React.ReactElement {
  const [expanded, setExpanded] = React.useState(() =>
    readResearchDisclaimerExpanded(kind)
  );

  const body = expandedBody ?? EXPANDED_BY_KIND[kind];

  const onOpenChange = React.useCallback(
    (next: boolean) => {
      setExpanded(next);
      writeResearchDisclaimerExpanded(kind, next);
    },
    [kind]
  );

  const toggle = () => onOpenChange(!expanded);

  const disclaimerBody = !expanded ? (
    <AlertDescription className={DISCLAIMER_BODY_CLASS}>
      {RESEARCH_DISCLAIMER_COLLAPSED}
    </AlertDescription>
  ) : (
    <CollapsibleContent>
      <AlertDescription className={DISCLAIMER_BODY_CLASS}>{body}</AlertDescription>
    </CollapsibleContent>
  );

  return (
    <Alert
      variant="info"
      data-research-disclaimer={kind}
      className={cn(
        "grid w-full grid-cols-1 gap-2 rounded-lg font-[family-name:var(--font-primary)] md:grid-cols-[auto_1fr] md:gap-x-2",
        className
      )}
    >
      <div className="flex w-full items-center justify-between gap-2 md:contents">
        <Info
          aria-hidden
          className="size-4 shrink-0 text-[color:var(--brand-tropical-surf)] md:row-span-2 md:translate-y-0.5"
        />
        <DisclaimerToggleButton
          expanded={expanded}
          onClick={toggle}
          className="md:hidden"
        />
      </div>

      <Collapsible
        open={expanded}
        onOpenChange={onOpenChange}
        className="min-w-0 w-full md:col-start-2"
      >
        <div className="flex w-full items-start gap-3">
          <div className="min-w-0 w-full flex-1">{disclaimerBody}</div>
          <DisclaimerToggleButton
            expanded={expanded}
            onClick={toggle}
            className="hidden md:inline-flex"
          />
        </div>
      </Collapsible>
    </Alert>
  );
}
